import { t } from "../../i18n";
import { useEffect, useState } from "react";
import { Bot, LoaderCircle, Sparkles } from "lucide-react";
import type { Conversation } from "@lumen/shared";
import { api } from "../../api";
import { MessageCard } from "../../components/chat/MessageCard";
import { TodoPanel } from "../../components/chat/TodoPanel";
import type { DisplayMessage } from "./ChatPage";

export function SharedChatPage({ token }: { token: string }) {
  const [conversation, setConversation] = useState<Conversation | null>();
  const [error, setError] = useState("");

  useEffect(() => {
    api<Conversation>(`/public/shares/${encodeURIComponent(token)}`)
      .then(setConversation)
      .catch((reason) =>
        setError(
          reason instanceof Error
            ? reason.message
            : "Could not load shared conversation",
        ),
      );
  }, [token]);

  if (error)
    return (
      <main className="grid min-h-screen place-items-center bg-[#f7faf8] p-6 dark:bg-[#0d1210]">
        <div className="panel max-w-md p-8 text-center">
          <Bot className="mx-auto text-stone-400" size={32} />
          <h1 className="mt-4 text-xl font-bold">
            {t("This shared chat is unavailable")}
          </h1>
          <p className="mt-2 text-sm leading-6 text-stone-500">{error}</p>
        </div>
      </main>
    );
  if (!conversation)
    return (
      <main className="grid min-h-screen place-items-center">
        <LoaderCircle className="animate-spin text-lumen-600" size={28} />
      </main>
    );

  return (
    <div className="h-[100dvh] overflow-y-auto bg-[#f7faf8] dark:bg-[#0d1210]">
      <header className="sticky top-0 z-10 border-b border-stone-200 bg-white/90 px-5 py-3 backdrop-blur dark:border-white/10 dark:bg-[#111715]/90">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <span className="grid size-9 place-items-center rounded-xl bg-lumen-600 text-white">
            <Sparkles size={18} />
          </span>
          <div className="min-w-0">
            <div className="truncate font-bold">{conversation.title}</div>
            <div className="text-xs text-stone-400">
              {t("Shared read-only chat")}
            </div>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl py-5">
        {conversation.todoList && (
          <TodoPanel snapshot={conversation.todoList} readOnly />
        )}
        {conversation.messages?.map((message) => (
          <MessageCard
            key={message.id}
            message={message as DisplayMessage}
            agents={[]}
            user={{ name: "User", avatarUrl: null }}
            busy={false}
            decidingApprovalIds={new Set()}
            approvals={new Map()}
            readOnly
          />
        ))}
      </main>
    </div>
  );
}
